import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { PrimaryUserInfo, PrimaryUserRole } from '../../../../types/clientOnboarding';

interface Props {
  data: Partial<PrimaryUserInfo>;
  onChange: (data: Partial<PrimaryUserInfo>) => void;
  errors: Record<string, string>;
}

const inputCls =
  'w-full p-2.5 border border-[var(--border-light)] rounded-lg bg-[var(--surface)] text-[var(--text-primary)] focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent';
const labelCls = 'block text-sm font-medium text-[var(--text-secondary)] mb-1';
const errorCls = 'text-[var(--color-danger)] text-xs mt-1';

const ROLES: PrimaryUserRole[] = ['owner', 'farm_manager', 'technician', 'viewer'];

const PrimaryUserStep: React.FC<Props> = ({ data, onChange, errors }) => {
  const { t } = useTranslation(['admin_client_onboarding']);

  const roleLabels: Record<PrimaryUserRole, string> = {
    owner: t('step3.roles.owner'),
    farm_manager: t('step3.roles.farm_manager'),
    technician: t('step3.roles.technician'),
    viewer: t('step3.roles.viewer'),
  };

  const set = (key: keyof PrimaryUserInfo, value: string) =>
    onChange({ ...data, [key]: value });

  return (
    <div className="space-y-4" data-testid="onboarding-step-user">
      <div>
        <h3 className="text-heading-3 text-[var(--text-primary)] mb-1">{t('step3.heading')}</h3>
        <p className="text-sm text-[var(--text-secondary)]">{t('step3.description')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls} htmlFor="user-first-name">{t('step3.first_name')} *</label>
          {/* R2R-P2-02: Render any first_name error — missing (primary_user.first_name)
              or over-length (primary_user.first_name_max) — through a single inline path. */}
          {(() => {
            const nameError = errors['primary_user.first_name'] || errors['primary_user.first_name_max'];
            return (
              <>
                <input id="user-first-name" className={inputCls} value={data.first_name || ''}
                  onChange={e => set('first_name', e.target.value)}
                  placeholder={t('step3.first_name_ph')} data-testid="onboarding-user-firstname"
                  aria-invalid={nameError ? true : undefined}
                  aria-describedby={nameError ? 'user-first-name-err' : undefined} />
                {nameError && <p id="user-first-name-err" className={errorCls} role="alert">{nameError}</p>}
              </>
            );
          })()}
        </div>
        <div>
          <label className={labelCls} htmlFor="user-last-name">{t('step3.last_name')} *</label>
          {(() => {
            const nameError = errors['primary_user.last_name'] || errors['primary_user.last_name_max'];
            return (
              <>
                <input id="user-last-name" className={inputCls} value={data.last_name || ''}
                  onChange={e => set('last_name', e.target.value)}
                  placeholder={t('step3.last_name_ph')} data-testid="onboarding-user-lastname"
                  aria-invalid={nameError ? true : undefined}
                  aria-describedby={nameError ? 'user-last-name-err' : undefined} />
                {nameError && <p id="user-last-name-err" className={errorCls} role="alert">{nameError}</p>}
              </>
            );
          })()}
        </div>
        <div>
          <label className={labelCls} htmlFor="user-email">{t('step3.email')} *</label>
          <input id="user-email" className={inputCls} type="email" value={data.email || ''}
            onChange={e => set('email', e.target.value)}
            placeholder={t('step3.email_ph')} data-testid="onboarding-user-email"
            aria-describedby={errors['primary_user.email'] ? 'user-email-err' : undefined} />
          {errors['primary_user.email'] && <p id="user-email-err" className={errorCls} role="alert">{errors['primary_user.email']}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="user-role">{t('step3.role')}</label>
          <select id="user-role" className={inputCls} value={data.role || 'farm_manager'}
            onChange={e => set('role', e.target.value as PrimaryUserRole)}
            data-testid="onboarding-user-role">
            {ROLES.map(r => (
              <option key={r} value={r}>{roleLabels[r]}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="onboarding-no-cognito-note">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">{t('step3.noCognitoNote')}</p>
          <p>{t('step3.noInviteNote')}</p>
        </div>
      </div>
    </div>
  );
};

export default PrimaryUserStep;
